import React, { useEffect, useState } from 'react';
import ConfirmModal from '../common/ConfirmModal';
import {
  connectGooglePlugin,
  disconnectGooglePlugin,
  getGooglePluginStatus,
  syncGoogleCalendar,
} from '../../services/googlePluginService';

const GoogleCalendarIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <rect x="4" y="6" width="24" height="20" rx="3" fill="#1A73E8" />
    <rect x="4" y="6" width="24" height="6" fill="#188038" />
    <text x="16" y="22" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="sans-serif">
      31
    </text>
  </svg>
);

const GoogleCalendarPanel = ({ onStatusChange } = {}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(0);
  const [syncTotal, setSyncTotal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const loadStatus = async () => {
    try {
      const data = await getGooglePluginStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    onStatusChange?.(Boolean(status?.calendarConnected));
  }, [status, onStatusChange]);

  const handleConnect = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await connectGooglePlugin('calendar');
      const data = await getGooglePluginStatus();
      setStatus(data);
      if (!data?.calendarConnected) {
        throw new Error('Google connected, but Calendar is not available yet. Click Connect again.');
      }
      setSuccess('Google Calendar connected.');
      try {
        setSyncing(true);
        setSyncDone(0);
        setSyncTotal(0);
        const result = await syncGoogleCalendar((done, total) => {
          setSyncDone(done);
          setSyncTotal(total);
        });
        window.dispatchEvent(new Event('workloop:calendar-changed'));
        setSuccess(
          `Google Calendar connected. Synced ${result.imported || 0} meetings from the next 7 days.`
        );
      } catch (syncError) {
        setSuccess('Google Calendar connected. Open this plugin and click Sync now if events are missing.');
        setError(syncError.message || 'Connected, but sync failed.');
      }
    } catch (e) {
      setError(e.message || 'Could not connect Google Calendar.');
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncDone(0);
    setSyncTotal(0);
    setError('');
    setSuccess('');
    try {
      const result = await syncGoogleCalendar((done, total) => {
        setSyncDone(done);
        setSyncTotal(total);
      });
      await loadStatus();
      window.dispatchEvent(new Event('workloop:calendar-changed'));
      setSuccess(
        `Synced the next 7 days. Imported ${result.imported || 0}, updated ${result.updated || 0}`
          + (result.skipped ? `, skipped ${result.skipped}` : '')
          + '. Open My Calendar to see them.'
      );
    } catch (e) {
      setError(e.message || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await disconnectGooglePlugin('calendar');
      await loadStatus();
      setSuccess('Google Calendar disconnected.');
    } catch (e) {
      setError(e.message || 'Could not disconnect.');
    } finally {
      setLoading(false);
      setConfirmDisconnect(false);
    }
  };

  const connected = Boolean(status?.calendarConnected);
  const oauthAvailable = status?.oauthLoginAvailable !== false;

  return (
    <div>
      {error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          {success}
        </div>
      ) : null}

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        Sync now pulls Google meetings for the next 7 days into WorkLoop. WorkLoop invites are written to Google at the date and time you set, and Google sends the invite to participants.
      </p>

      {connected ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Integrated</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            {status.displayName || status.email || 'Google account'}
            {status.email && status.displayName ? ` · ${status.email}` : ''}
          </p>
          {status.calendarLastSyncedAt ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Last sync: {status.calendarLastSyncedAt.replace('T', ' ')}
            </p>
          ) : null}
          {syncing ? (
            <div className="mt-4 rounded-xl border border-blue-100 dark:border-blue-900/50 bg-blue-50/80 dark:bg-blue-950/30 px-3 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <GoogleCalendarIcon className="w-5 h-5 shrink-0" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {syncTotal > 0 ? `${syncDone}/${syncTotal} synced` : 'Starting sync…'}
                </p>
                <span className="ml-auto text-xs font-medium text-gray-500">
                  {syncTotal > 0 ? `${Math.min(100, Math.round((syncDone / syncTotal) * 100))}%` : ''}
                </span>
              </div>
              <div className="h-2 rounded-full bg-white dark:bg-gray-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#1A73E8] transition-all duration-300"
                  style={{ width: `${syncTotal > 0 ? Math.min(100, Math.round((syncDone / syncTotal) * 100)) : 8}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Syncing the next 7 days of meetings…
              </p>
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <GoogleCalendarIcon className="w-4 h-4" />
              {syncing && syncTotal > 0 ? `${syncDone}/${syncTotal} synced` : syncing ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              type="button"
              onClick={handleConnect}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              Reconnect
            </button>
            <button
              type="button"
              onClick={() => setConfirmDisconnect(true)}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-rose-200 text-rose-700 dark:border-rose-800 dark:text-rose-300"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {oauthAvailable
              ? 'Sign in with Google to sync your WorkLoop calendar.'
              : 'Google OAuth is not configured on the server. Set GOOGLE_OAUTH_CLIENT_ID and GOOGLE_OAUTH_CLIENT_SECRET.'}
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={loading || !oauthAvailable}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? 'Connecting…' : 'Connect Google Calendar'}
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Google Calendar"
        message="WorkLoop meetings will stop updating Google Calendar. Existing events stay on Google until you delete them there."
        confirmText="Disconnect"
        type="danger"
      />
    </div>
  );
};

export default GoogleCalendarPanel;
